import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  FormControlLabel,
  Checkbox,
  Paper,
} from "@material-ui/core";
import { CloudUpload, GetApp, CheckCircle, Error, Warning } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { green, red } from "@material-ui/core/colors";
import axios from "axios";

const useStyles = makeStyles((theme) => ({
  uploadBox: {
    border: "2px dashed #ccc",
    borderRadius: "4px",
    padding: theme.spacing(4),
    textAlign: "center",
    cursor: "pointer",
    transition: "border-color 0.3s ease",
    "&:hover": {
      borderColor: "#009E60",
    },
    "&.dragOver": {
      borderColor: "#009E60",
      backgroundColor: "#f5f5f5",
    },
    "&.disabled": {
      cursor: "not-allowed",
      opacity: 0.6,
      backgroundColor: "#f5f5f5",
      "&:hover": {
        borderColor: "#ccc",
      },
    },
  },
  fileInput: {
    display: "none",
  },
  uploadIcon: {
    fontSize: "3rem",
    color: "#009E60",
    marginBottom: theme.spacing(1),
  },
  resultSection: {
    marginTop: theme.spacing(2),
  },
  successChip: {
    backgroundColor: green[500],
    color: "white",
  },
  errorChip: {
    backgroundColor: red[500],
    color: "white",
  },
  downloadTemplate: {
    marginBottom: theme.spacing(2),
  },
  successAlert: {
    backgroundColor: green[50],
    border: `1px solid ${green[200]}`,
    padding: theme.spacing(2),
    borderRadius: "4px",
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
  },
  errorAlert: {
    backgroundColor: red[50],
    border: `1px solid ${red[200]}`,
    padding: theme.spacing(2),
    borderRadius: "4px",
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
  },
  alertIcon: {
    marginRight: theme.spacing(1),
  },
  warningAlert: {
    backgroundColor: "#fff3cd",
    border: "1px solid #ffeaa7",
    padding: theme.spacing(2),
    borderRadius: "4px",
    marginBottom: theme.spacing(2),
  },
  warningBox: {
    backgroundColor: "#f8f9fa",
    border: "1px solid #dee2e6",
    padding: theme.spacing(2),
    borderRadius: "4px",
    marginBottom: theme.spacing(2),
  },
}));

const BulkUploadDialog = ({ open, onClose, onUploadSuccess, axiosConfig }) => {
  const classes = useStyles();
  const MAX_FILE_BYTES = 25 * 1024 * 1024;
  const MAX_FILE_MB = 25;
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);

  const handleFileSelect = (file) => {
    if (uploading) return;

    const allowedTypes = [".csv", ".xlsx", ".xls"];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf("."));

    if (allowedTypes.includes(fileExt)) {
      if (file.size > MAX_FILE_BYTES) {
        alert(`File is too large. Maximum size is ${MAX_FILE_MB}MB.`);
        return;
      }
      setSelectedFile(file);
      setUploadResult(null);
    } else {
      alert("Please select a CSV or XLSX file");
    }
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    if (uploading) return;
    const file = event.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!uploading) setDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleUploadBoxClick = () => {
    if (uploading) return;
    document.getElementById("roadmap-bulk-file-input").click();
  };

  const bulkUploadFile = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("updateExisting", updateExisting ? "true" : "false");
      formData.append("skipDuplicates", updateExisting ? "false" : "true");

      const response = await axios.post(
        "/roadmap-template/admin/bulk-upload-file",
        formData,
        {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setUploadResult(response.data);
      if (onUploadSuccess) onUploadSuccess(response.data);
    } catch (error) {
      const payload = error.response?.data || {};
      setUploadResult({
        error: payload.message || error.message || "Upload failed",
        validationErrors: payload.validationErrors || [],
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setUploading(false);
    setUpdateExisting(false);
    onClose();
  };

  const downloadTemplate = async (format) => {
    try {
      const response = await axios.get(
        `/roadmap-template/admin/bulk-upload-template?format=${format}`,
        {
          ...axiosConfig,
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        format === "csv"
          ? "roadmap-template-import-template.csv"
          : "roadmap-template-import-template.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(error.response?.data?.message || "Failed to download template");
    }
  };

  const summary = uploadResult?.data?.summary;
  const validationErrors = uploadResult?.data?.validationErrors || uploadResult?.validationErrors || [];
  const hasIssues =
    (summary?.validationErrors > 0) ||
    (summary?.templatesFailed > 0) ||
    (summary?.templatesSkipped > 0);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <CloudUpload style={{ marginRight: "8px" }} />
          Bulk Upload Roadmap Templates
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box className={classes.downloadTemplate}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Download the template (includes sample data on the Import_Tasks sheet):
          </Typography>
          <Box display="flex" style={{ gap: "8px", flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={<GetApp />}
              onClick={() => downloadTemplate("xlsx")}
              size="small"
            >
              Download XLSX Template
            </Button>
            <Button
              variant="outlined"
              startIcon={<GetApp />}
              onClick={() => downloadTemplate("csv")}
              size="small"
            >
              Download CSV Template
            </Button>
          </Box>
        </Box>

        <Divider style={{ margin: "16px 0" }} />

        <div className={classes.warningAlert}>
          <Typography variant="body2" style={{ color: "#856404" }}>
            <strong>Format:</strong> One row per task. Use the same <code>templateKey</code> for
            all rows in a template. Sheet name should be <strong>Import_Tasks</strong> (or the first
            sheet will be used).
          </Typography>
        </div>

        <Paper
          className={`${classes.uploadBox} ${dragOver && !uploading ? "dragOver" : ""} ${
            uploading ? "disabled" : ""
          }`}
          elevation={0}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleUploadBoxClick}
        >
          <CloudUpload className={classes.uploadIcon} style={{ opacity: uploading ? 0.5 : 1 }} />
          <Typography variant="h6" gutterBottom style={{ opacity: uploading ? 0.5 : 1 }}>
            {uploading
              ? "Import in progress..."
              : selectedFile
              ? selectedFile.name
              : "Drop your file here or click to browse"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Supported formats: CSV, XLSX, XLS (max {MAX_FILE_MB}MB)
          </Typography>
        </Paper>

        <input
          id="roadmap-bulk-file-input"
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileInputChange}
          className={classes.fileInput}
          disabled={uploading}
        />

        <Box mt={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={updateExisting}
                onChange={(e) => setUpdateExisting(e.target.checked)}
                color="primary"
                disabled={uploading}
              />
            }
            label="Update existing templates when name matches (otherwise skip duplicates)"
          />
        </Box>

        {uploading && (
          <Box mt={2}>
            <LinearProgress />
            <Typography variant="caption" color="textSecondary">
              Grouping rows and creating templates…
            </Typography>
          </Box>
        )}

        {uploadResult && (
          <Box className={classes.resultSection}>
            {uploadResult.error ? (
              <div className={classes.errorAlert}>
                <Error className={classes.alertIcon} style={{ color: red[500] }} />
                <Box>
                  <Typography variant="subtitle2">Upload Failed</Typography>
                  <Typography variant="body2">{uploadResult.error}</Typography>
                </Box>
              </div>
            ) : (
              <div className={classes.successAlert}>
                <CheckCircle className={classes.alertIcon} style={{ color: green[500] }} />
                <Typography variant="subtitle2">
                  {hasIssues ? "Import completed with warnings" : "Import successful"}
                </Typography>
              </div>
            )}

            {summary && (
              <Box display="flex" flexWrap="wrap" mb={2} style={{ gap: "8px" }}>
                <Chip label={`Rows: ${summary.totalRows || 0}`} size="small" />
                <Chip label={`Templates in file: ${summary.templatesInFile || 0}`} size="small" />
                <Chip
                  label={`Created: ${summary.templatesCreated || 0}`}
                  size="small"
                  className={classes.successChip}
                />
                {summary.templatesUpdated > 0 && (
                  <Chip
                    label={`Updated: ${summary.templatesUpdated}`}
                    size="small"
                    className={classes.successChip}
                  />
                )}
                {summary.templatesSkipped > 0 && (
                  <Chip label={`Skipped: ${summary.templatesSkipped}`} size="small" />
                )}
                {summary.templatesFailed > 0 && (
                  <Chip
                    label={`Failed: ${summary.templatesFailed}`}
                    size="small"
                    className={classes.errorChip}
                  />
                )}
                {summary.validationErrors > 0 && (
                  <Chip label={`Validation issues: ${summary.validationErrors}`} size="small" />
                )}
              </Box>
            )}

            {validationErrors.length > 0 && (
              <div className={classes.warningBox}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Warning style={{ color: "#6c757d", marginRight: 8 }} />
                  <Typography variant="subtitle2">Validation errors</Typography>
                </Box>
                <List dense style={{ maxHeight: 200, overflow: "auto" }}>
                  {validationErrors.slice(0, 10).map((item, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={`${item.templateKey || item.name}: ${(item.errors || []).join("; ")}`}
                        secondary={
                          item.rows?.length ? `Rows: ${item.rows.join(", ")}` : null
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </div>
            )}

            {uploadResult?.data?.failed?.length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Failed imports
                </Typography>
                <List dense>
                  {uploadResult.data.failed.slice(0, 5).map((item, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={item.name || item.templateKey}
                        secondary={item.error}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={uploading}>
          Close
        </Button>
        {selectedFile && !uploading && !uploadResult && (
          <Button
            onClick={bulkUploadFile}
            color="primary"
            variant="contained"
            style={{ backgroundColor: "#009E60" }}
          >
            Upload File
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkUploadDialog;
